import React from "react";
import { Answer, LocaleValue } from "../../types";
import Mp4Video from "./mp4Video";
import { useApp } from "../../state/app";
import { MODE } from "../../config/consts";
import scorm from "../../services/scorm";

type Props = {
  sceneId: string;
  videoSrc: string;
  subtitle: string;
  answers?: Answer[];
  answersTime?: number;
  answersShow?: string;
  countDownColor?: string;
  forceSubtitles?: boolean | { [locale: string]: boolean };
  skippableVideo: boolean;
  brightcove?: string;
  question?: LocaleValue;
  lastScene?: boolean;
};

export default function Video({
  sceneId,
  videoSrc,
  subtitle,
  answers,
  answersTime,
  answersShow,
  skippableVideo,
  brightcove,
  question,
  forceSubtitles = false,
  lastScene = false,
}: Props) {
  const { language, videoTranscript } = useApp();
  const [src, setSrc] = React.useState<string>(videoSrc);
  const [currentSubtitle, setCurrentSubtitle] =
    React.useState<string>(subtitle);

  const videoInfo = React.useMemo(() => {
    if (!brightcove) return null;
    const matches = brightcove?.match(/\d+/g) ?? [];
    return { accountId: matches[0] ?? "", videoId: matches[1] ?? "" };
  }, [brightcove]);

  React.useEffect(() => {
    if (videoInfo === null) return;
    (async () => {
      try {
        const res = await fetch(
          `https://edge.api.brightcove.com/playback/v1/accounts/${videoInfo.accountId}/videos/${videoInfo.videoId}`,
          {
            headers: {
              accept: `application/json;pk=BCpkADawqM0S8AeV94J3MZMt07XnlPLBdWO_LTAORf4ND9goQ-BJ6EsMA4cnbUuEl5EKtsZdFBB2YU7lFGhXuVoddVEpNwoQnIckWThXSOAqrV2669At6MxBI47DAOxib6SwdanHV0SYTVsm`,
            },
          },
        );
        const data = await res.json();
        const mp4 = data.sources.find((source: any) => {
          return source.container === "MP4" && source.src.includes("https");
        });
        setSrc(mp4?.src);
        const fallback = data.text_tracks?.[0] ?? { srclang: null };
        let fallbackSrc = "";
        if (fallback.srclang === null) {
          fallbackSrc = fallback.src;
        }
        const languageMap = {
          en: "en",
          "es-419": "es",
          fr: "fr",
          "fr-CA": "fr-ca",
          "zh-Hant": "zh",
        };
        const subtitle =
          (data.text_tracks ?? []).find((track: any) => {
            // @ts-ignore
            const lang = languageMap[track.srclang];
            return lang === language;
          }) ?? {};
        const subtitleSource = subtitle?.sources?.find((source: any) =>
          source?.src?.includes("https"),
        );
        setCurrentSubtitle(
          subtitleSource.src ?? subtitle?.src ?? subtitle ?? fallbackSrc,
        );
      } catch (error) {
        console.error(error);
      }
    })();
  }, [videoInfo, language, subtitle]);

  React.useEffect(() => {
    if (videoTranscript === null) return;
    if (videoInfo === null) {
      const transcript = videoTranscript?.[language];
      setCurrentSubtitle(transcript);
    }
  }, [videoInfo, videoTranscript])

  React.useEffect(() => {
    if (!MODE.startsWith("scorm")) return;
    if (!scorm.isConnected()) return;
    if (lastScene) {
      scorm.complete();
    }
  }, [lastScene]);

  return (
    <Mp4Video
      sceneId={sceneId}
      videoSrc={src}
      subtitle={currentSubtitle}
      answers={answers}
      answersTime={answersTime}
      answersShow={answersShow}
      skippableVideo={skippableVideo}
      forceSubtitles={forceSubtitles}
      question={question?.[language]}
    />
  );
}
